<script lang="ts" setup>
import PageHeader from '@/components/header/PageHeader.vue'
import { NewsItem } from '@/helper/Types'
import { useRoute } from 'vue-router'
import { computed, onMounted, ref } from 'vue'
import NewsEditList from '@/components/admin/NewsEditList.vue'
import { setMetaTitle } from '@/helper/tools'
import { api } from '@/services/ApiService'
import { Locale } from '@/helper/Locale'
import NewsApi from '@/services/NewsApi'

const route = useRoute()
const country = route.params.countryCode.toString() || ''
const brand = route.params.brand.toString() || ''
const news = ref<NewsItem[]>([])
let availableLanguages: string[] = []

setMetaTitle('Edit News Page')

onMounted(async () => {
  const translations = await api.loadAvailableAdminTranslations()

  availableLanguages = translations
    .filter(t => t.brand === brand)
    .map(t => new Locale(t.locale))
    .filter(l => l.country === country)
    .map(l => l.language)

  news.value = await NewsApi.fetchNewsByBrandAndCountry(brand, country)
})

function addNews() {
  const emptyLocalizedNewsObject = availableLanguages.map(language => {
    return {
      text: '',
      language: language
    }
  })

  const newsObj: NewsItem = {
    createdAt: 0,
    localizedNews: [...emptyLocalizedNewsObject],
    brand: brand,
    country: country
  }

  news.value.unshift(newsObj)
}

const hasUnsavedNews = computed(() => news.value.some(n => !n.id))

async function saveNews(editedNews: NewsItem) {
  let savedItem: NewsItem
  if (editedNews.id === undefined) {
    savedItem = await NewsApi.createNews(editedNews)
  } else {
    let id = editedNews.id
    editedNews.id = undefined //to disable save button
    savedItem = await NewsApi.updateNews({ ...editedNews, id })
  }
  const indexToReplace = news.value.indexOf(editedNews)
  news.value[indexToReplace] = savedItem
}

function deleteNews(deletedNews: NewsItem) {
  const indexToReplace = news.value.indexOf(deletedNews)

  if (deletedNews.id !== undefined) {
    NewsApi.deleteNews(deletedNews.id)
      .then(() => news.value.splice(indexToReplace, 1))
      .catch(e => console.error(e))
  } else {
    news.value.splice(indexToReplace, 1)
  }
}
</script>

<template>
  <div>
    <PageHeader />
    <div class="main-wrapper">
      <div class="container main">
        <div class="config-sections-container">
          <div class="form">
            <div class="news-edit-page-header">
              <h2>Edit News</h2>
              <button :disabled="hasUnsavedNews || availableLanguages.length < 1" class="btn add-btn" @click="addNews">
                Create news
              </button>
            </div>

            <NewsEditList :news="news" @delete="item => deleteNews(item)" @save="item => saveNews(item)" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.form {
  max-width: 100%;
}

.news-edit-page-header {
  display: flex;
  margin-bottom: 2rem;
}

.add-btn {
  display: flex;
  margin-left: auto;
}
</style>
